/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HeaderModule
 */

import {
    Component,
    Injectable,
    Type,
} from '@angular/core';

import { Subject } from 'rxjs';
import { FullModalService, IFullModalLayout } from 'full-modal-service';
import { L10nService } from '@vmw/ngx-vip';

import {
    MyAccountModalComponent,
    MyAccountPreviewComponent,
} from 'ng/modules/accounts/components/my-account-modal';

import * as l10n from './avi-main-header-user-menu.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Service for the AviMainHeaderUserMenu component.
 * @author Nisar Nadaf, alextsg
 */
@Injectable()
export class AviMainHeaderUserMenuService {
    /**
     * Observable used to display the User Account full name in the breadcrumb description. User can
     * modify the full name in the modal.
     */
    public description$: Subject<string>;

    constructor(
        private readonly fullModalService: FullModalService,
        private readonly l10nService: L10nService,
    ) {}

    /**
     * Open "My Account" modal.
     */
    public openMyAccountModal(): void {
        this.description$ = new Subject<string>();
        this.fullModalService.addModal(this.getMyAccountModalProps());
    }

    /**
     * Close "My Account" modal.
     */
    public closeMyAccountModal(): void {
        this.description$.complete();
        this.fullModalService.removeModalByComponent(MyAccountModalComponent as Type<Component>);
    }

    /**
     * Check if "My Account" modal is open.
     */
    public isMyAccountModalOpen(): boolean {
        return this.fullModalService.isOpen(MyAccountModalComponent as Type<Component>);
    }

    /**
     * Return props to be passed to the FullModal.
     */
    private getMyAccountModalProps(): IFullModalLayout {
        let description: string;
        const descriptionSubscription = this.description$.subscribe(desc => description = desc);

        return {
            component: MyAccountModalComponent as Type<Component>,
            componentProps: {
                description$: this.description$,
                onClose: () => {
                    this.closeMyAccountModal();
                    descriptionSubscription.unsubscribe();
                },
            },
            previewComponent: MyAccountPreviewComponent as Type<Component>,
            getName: () => this.l10nService.getMessage(l10nKeys.myAccountLabel),
            getDescription: () => description,
        };
    }
}
